import { Link as RouterLink } from 'react-router';

import { Button } from '@/ui/button-2.tsx';

import Circle from '../../components/Circle/index.ts';
import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import useBrainSession from '../../services/BrainSession/useBrainSession.ts';
import { LANG } from '../../services/Config.js';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_DEBT_INFORMATION } from './RRS.ts';

const linkClassName =
  'typography-v1-legacy-p3 font-normal text-[#18A0FB]! no-underline hover:underline';

const chatTriggerButtonClassName = `${linkClassName} inline bg-transparent border-0 p-0 cursor-pointer text-left font-inherit`;

const articleRowClassName = 'mx-auto flex w-full max-w-[900px]! flex-col flex-wrap sm:flex-row';

const heroButtonClassName = 'max-w-[550px] self-center sm:min-w-[330px]';

const DebtInformation: React.FC = () => {
  const { routes, appStrings } = LANG;
  const { toggleChat } = useBrainSession();

  const openHeroDebtCheck = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'skip_start',
    });
  };

  const openInlineDebtCheck = () => {
    toggleChat({
      goal: 'debt_check',
      step: 'refin_present_terms_auto_new',
    });
  };

  const openFooterChat = () => {
    toggleChat({
      goal: 'refin_nogi',
      step: 'skip_start',
    });
  };

  return (
    <Page title={appStrings.PAGES.DEBT_INFORMATION}>
      <RrsHandler {...RRS_DEBT_INFORMATION} />
      <Section background="primary">
        <Circle
          className="pointer-events-none top-[-180px]! left-[-130px]! hidden lg:block"
          size={380}
          border={80}
          color="lightTransparent"
        />
        <Circle
          className="pointer-events-none top-[160px]! right-[-190px]! hidden lg:block"
          size={380}
          border={80}
          color="lightTransparent"
        />

        <div className="relative z-2 mx-auto flex w-full max-w-[900px]! flex-col text-white">
          <h1 className="mb-6 typography-v1-legacy-h2 sm:mb-8">
            Norsk Gjeldsinformasjon – all din usikrede gjeld på ett sted
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Hvor mye gjeld har jeg på kredittkort, faktureringskort, forbrukslån og rammekreditter?
            Hvem skylder jeg penger til? Hvilke selskaper kan se informasjon om gjelden min? Alt
            dette og mere til får du svar på hos Norsk Gjeldsinformasjon.
          </h2>
          <p className="mb-8 typography-v1-legacy-subtitle2 sm:mb-14">
            Selv om du har god oversikt over økonomien din, kan det være du har noen gamle lån eller
            kreditter som har gått i glemmeboken. Derfor kan det være smart å sjekke hvor mye du har
            i gjeld. Men hvor skal du lete? Den jobben har Norsk Gjeldsinformasjon allerede gjort
            for deg.
          </p>
          <div className="flex justify-center">
            <Button color="yellow" className={heroButtonClassName} onClick={openHeroDebtCheck}>
              Sjekk din gjeld
            </Button>
          </div>
        </div>
      </Section>
      <Section background="paper" last>
        <div className={articleRowClassName}>
          <div className="w-full sm:w-1/2 sm:pr-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hva er Norsk Gjeldsinformasjon?
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Det er de største bankene i Norge som tilbyr kreditt i personmarkedet som eier Norsk
              Gjeldsinformasjon. Her får du trygt, raskt og enkelt oversikt over den usikrede
              gjelden din – bortsett fra studielån. Det betyr at du ikke finner gjeld dersom du har
              lån du har stilt sikkerhet i – som bolig, hytte, bil og motorsykkel. På sikt håper de
              å få med sikret gjeld i oversikten.
              <br />
              <br />
              Norsk Gjeldsinformasjon kan for øvrig ikke se hvor mye usikret gjeld du har. Det kan
              kun finansforetaket du søker om kreditt eller lån hos, men de kan ikke se hvem du har
              gjeld til.
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Hvordan fungerer Norsk Gjeldsinformasjon?
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              De aller fleste finansforetak leverer data til Norsk Gjeldsinformasjon, og{' '}
              <a
                href="https://www.norskgjeld.no/for-bankene"
                rel="noopener noreferrer"
                target="_blank"
                className={linkClassName}
              >
                her får du en oversikt over hvilke
              </a>
              . Dersom du ikke finner alle typer usikret gjeld du har, kan det være finansforetaket
              du har brukt enten ikke har oppdatert informasjonen om deg, eller ikke har levert
              denne selv om de er forpliktet til å gjøre det.
              <br />
              <br />
              Norsk Gjeldsinformasjon fungerer også motsatt vei, altså at finansforetak får raskere
              oversikt over økonomien din. Det gjør at de kan foreta en bedre{' '}
              <RouterLink className={linkClassName} to={routes.CREDIT_CHECK_MYSELF}>
                kredittvurdering
              </RouterLink>{' '}
              av deg når du søker om for eksempel forbrukslån eller kredittkort.
              <br />
              <br />
              Merk at gjeldsinformasjonen din kun formidles til finansforetak,
              kredittopplysningsselskaper – og offentlige etater. Det betyr at andre som vil gjøre
              en kredittvurdering, som når du skal kjøpe en vare mot faktura eller på avbetaling,
              ikke kan motta gjeldsinformasjon om deg.
              <br />
              <br />
              Har du gjeldsinformasjon, kan du ikke reservere deg mot at denne blir utlevert. I
              henhold til gjeldsinformasjonsloven har nemlig alle finansforetak plikt til å utlevere
              all informasjon de har om den usikrede gjelden din.
            </p>
          </div>

          <div className="w-full sm:w-1/2 sm:pl-6">
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Logg inn på Norsk Gjeldsinformasjon
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Tjenesten er tilkoblet ID-porten, hvilket gjør at du trygt kan logge deg inn via for
              eksempel BankID – akkurat som lignende tjenester. Du trenger med andre ord ikke
              bekymre deg for at gjeldsinformasjon og personopplysninger om deg kommer på avveie.
              <br />
              <br />
              Når du har logget inn ser du hvor mye kreditt du har benyttet og hva du har i
              forbrukslån. Ønsker du for eksempel å slette et eller flere kredittkort, kan du ta
              kontakt med kortutstederne. Dette kan være et lurt trekk for økonomien din, da
              finansforetakene ser på den totale gjelden som ligger i kortene – og ikke hvor mye du
              faktisk har brukt.
            </p>

            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Vil du ha lavere renter på gjelden din?
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Med Norsk Gjeldsinformasjon får du altså raskt og enkelt oversikt over den usikrede
              gjelden din. Og mens du var inne på nettsiden – la du merke til rentene du betalte på
              kortene og lånene dine? Kanskje du betaler mer enn nødvendig? Du kan nemlig spare
              tusenvis av kroner hver måned med ett enkelt grep.
              <br />
              <br />
              <strong>
                Hos Scoopr kan du sjekke om du får lavere rente gjennom å samle gjelden din i ett
                nytt lån – det tar deg bare to minutter.
              </strong>
              <br />
              <br />
              <button
                type="button"
                className={chatTriggerButtonClassName}
                onClick={openInlineDebtCheck}
              >
                Her kan du logge deg på med BankID og finne ut om du kan spare med Scoopr
              </button>
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center">
          <h3 className="mx-auto mt-14 mb-8 max-w-[900px]! text-center typography-v1-legacy-subtitle1Bold sm:mt-[136px] sm:mb-8">
            Scoopr hjelper deg med det også!
          </h3>
          <Button color="yellow" className={heroButtonClassName} onClick={openFooterChat}>
            Sjekke om du kan få en bedre avtale
          </Button>
          <Disclaimer />
        </div>
      </Section>
    </Page>
  );
};

export default DebtInformation;
